import getAgendaStore from '../store/store';
import repeatActionEveryXMinutes from 'mini-refresh-timers/lib/utils/repeatActionEveryXMinutes';
import skipItemFromUpcomingOngoingTreatment from 'mini-agenda/lib/utils/skipItemFromUpcomingOngoingTreatment';
import { addDays, endOfDay, now, getTimestamp } from 'owa-datetime';
import { AgendaItem } from 'mini-agenda';
import { DAYS_TO_LOOKAHEAD_FOR_UPNEXT } from '../utils/agendaConstants';
import {
    updateOngoingAgendaItemId,
    updateRemainingMinutes,
    updateUpcomingAgendaItemId,
} from '../mutators/markerMutators';

// Shared variables
let nowMarkerTimer: NodeJS.Timer;
let upcomingMarkerTimer: NodeJS.Timer;
let upcomingTickingClock: NodeJS.Timer;

function testAndSetExpirationTimer(expirationTimeStamp: number): NodeJS.Timer {
    return setTimeout(setAgendaViewMarkers, expirationTimeStamp - getTimestamp(now()));
}

function testAndSetTickingClock(expirationTimeStamp: number, upcomingItem: AgendaItem) {
    if (!upcomingItem) {
        return;
    }
    /**
     * We could have directly used setInterval
     * But we have a very tricky edge case
     * Consider that the current time when the marker appeared on the screen at 19:15:35 hrs, and the next event is at 22:00:05 hrs
     * It means that we do a every minute using setInterval at the following times 19:15:35, 19:16:35, 19:17:35 and so on, and the time
     * update happens in a lag
     * An ideal experience which the customer would like is that the refresh first happens at 19:15:35 hrs, and then at 19:16:00 hrs ( which is less than a minute away ) and then every minute until expiry
     * We achieve this using a set of recurring setTimeout ( referring to file autoRefreshToday file ) which helps us as follows
     * ---> Add have a varying time interval
     * ---> Make this function self capable of clearing its timers
     */

    // The first trigger
    repeatActionEveryXMinutes(
        () => updateRemainingMinutes(expirationTimeStamp, upcomingItem.id),
        'updateRemainingMinutes',
        1 /**frequencyInMinutes */,
        expirationTimeStamp /**time of expiry */
    )();
}

function clearTimers() {
    nowMarkerTimer && clearTimeout(nowMarkerTimer);
    upcomingMarkerTimer && clearTimeout(upcomingMarkerTimer);
    upcomingTickingClock && clearInterval(upcomingTickingClock);
    nowMarkerTimer = undefined;
    upcomingMarkerTimer = undefined;
    upcomingTickingClock = undefined;
}

export default function setAgendaViewMarkers(ctx?: any) {
    clearTimers();

    const { items } = getAgendaStore();

    let upcomingItem: AgendaItem;
    let ongoingItem: AgendaItem;

    const cutoffDate = endOfDay(addDays(now(), DAYS_TO_LOOKAHEAD_FOR_UPNEXT));
    const cutoffTime: number = getTimestamp(cutoffDate);

    for (let index: number = 0; index < items.length; index++) {
        let item: AgendaItem = items[index];

        /*
        We will be skipping the initial set of items in the following cases
        - The item is a  header
        - The item is an all day event
        - The item occurs in the past ( which means it has already completed in the past )
        - TODO : The below conditions
        - We had an ongoingItem before and the current item is not same as the ongoing item - This is because we can safely skip all items till the ongoing item
        - We did not have an ongoing item but had an upcoming item and the current item is not same as the upcoming item - This is because we can safely skip all items till the upcoming item if ongoing item is missing
        */
        const shouldSkipItem: boolean =
            skipItemFromUpcomingOngoingTreatment(item) || item.event.IsPastEventInstance;

        if (shouldSkipItem) {
            continue;
        }

        // Check if we can stop our iteration
        if (item.event.IsInstanceStartingAfter(cutoffTime)) {
            // We have reached an item which will occur in a very far future and hence all items following it will follow suit
            break;
        }
        // Check if we can add a now marker to the event

        // First directly go to the latest item which is ongoing, that will be the one which recently started and which needs the
        // ongoing/now marker. But do this only if no event so far has been marked ongoing

        // This means that the first item we find now will be an ongoing or a future event
        // If we are entering the while loop, the item which we get in the first iteration
        // is definitely an ongoing item
        // From that first item , try to find the last item which is ongoing ( since the latest ongoing event needs to be marked)
        // On the way we might find an event which has started after the first ongoing item, but has completed by the current time
        // In that case we do not have the now marker on any item.
        // Hence the check for the past instance
        while (index < items.length && !skipItemFromUpcomingOngoingTreatment(items[index])) {
            item = items[index];
            index++;

            /**
             * Now check if we already have marked an item which is already completed at the current time
             * If that is the case, we do not have any ongoing event and hence we break
             */
            if (item.event.IsPastEventInstance) {
                ongoingItem = undefined;
            } else if (item.event.IsOngoingEventInstance) {
                // Check if the current event can be a potential on going event
                if (!(ongoingItem && ongoingItem.event.startsAlongWith(item.event))) {
                    // Make this item as the new ongoing item only if there is no other item starting at the same time as the currently chosen ongoing item
                    // This is to ensure that if there are multiple potential ongoing item candidates which start at the same, the first one visible in the
                    // agenda view is selected - Bug https://outlookweb.visualstudio.com/defaultcollection/Outlook%20Web/_workitems/edit/50068
                    ongoingItem = item;
                }
            } else {
                index--;
                break;
            }
        }

        // we might have a latest ongoing event in the item object
        if (ongoingItem) {
            const { event } = ongoingItem;

            // Determine the next item which comes, since that can be the potential next now event
            // This is needed to determine the expiration time for the current event marker
            let indexForNextEventAgendaItem = index + 1;
            let nextEventAgendaItem = items[indexForNextEventAgendaItem];
            while (
                indexForNextEventAgendaItem < items.length &&
                skipItemFromUpcomingOngoingTreatment(nextEventAgendaItem)
            ) {
                indexForNextEventAgendaItem++;
                nextEventAgendaItem = items[indexForNextEventAgendaItem];
            }

            // Set the properties for the item
            const expirationTimeStamp: number = Math.min(
                getTimestamp(event.End),
                nextEventAgendaItem
                    ? getTimestamp(nextEventAgendaItem.event.Start)
                    : getTimestamp(event.End)
            ); // this event's now marker can expire if the next event starts before this ends

            // Start a timer the timer for the now marker to change
            nowMarkerTimer = testAndSetExpirationTimer(expirationTimeStamp);
        }

        /*
         Check if we can add an upcoming marker to the event
         */
        const { event } = item;

        /**
         * Without this check, the currently marked ongoing event ( if any ) will get marked upcoming  which is wrong
         * And since we have filtered out far future items, the future event caught here is guaranteed to be within
         * the next 2 days
         */
        if (event.IsFutureEventInstance && !event.IsInstanceStartingAfter(cutoffTime)) {
            upcomingItem = item;
            const expirationTimeStamp = getTimestamp(event.Start);

            // Set the clock for the upcoming marker to change minutes
            testAndSetTickingClock(expirationTimeStamp, upcomingItem);

            // Set the timer for the upcoming marker to expire
            upcomingMarkerTimer = testAndSetExpirationTimer(expirationTimeStamp);
        }

        if (upcomingItem) {
            break;
            // stop prematurely since we have an upcoming, and now it doesn't matter whether we have ongoing or not, since its state will not change
        }
    }

    updateOngoingAgendaItemId(ongoingItem && ongoingItem.id);
    updateUpcomingAgendaItemId(
        upcomingItem && upcomingItem.id,
        upcomingItem &&
            upcomingItem.event &&
            getTimestamp(
                (upcomingItem.event.MultiDayInfo &&
                    upcomingItem.event.MultiDayInfo.OriginalEventStart) ||
                    upcomingItem.event.Start
            )
    );
    ctx &&
        ctx.signal('setAgendaViewMarkers', {
            ongoingItemId: ongoingItem && ongoingItem.id,
            upcomingItemId: upcomingItem && upcomingItem.id,
            cutoffDate: cutoffDate.toString(),
        });
}
